import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Coins, Palette, CheckCircle, FileText, CreditCard, Settings, Headphones, Sparkles, Zap, Shield, TrendingUp, Users, Award, Calendar } from 'lucide-react';
import { mockData } from '../mock';
import AnimatedBackground from '../components/AnimatedBackground';
import TechnologyMarquee from '../components/TechnologyMarquee';
import FAQSection from '../components/FAQSection';
import SEO from '../components/SEO';

const HomePage = () => {
  const { company, services, orderProcess } = mockData;
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  const fullText = company.tagline;

  // Typing animation
  useEffect(() => {
    if (isTyping && typedText.length < fullText.length) {
      const timeout = setTimeout(() => {
        setTypedText(fullText.slice(0, typedText.length + 1));
      }, 50);
      return () => clearTimeout(timeout);
    } else if (typedText.length === fullText.length) {
      setIsTyping(false);
    }
  }, [typedText, isTyping, fullText]);

  const serviceCards = [
    {
      title: services.website.title,
      subtitle: services.website.subtitle,
      icon: Globe,
      path: '/website-hizmetleri',
      gradient: 'from-blue-500 via-purple-500 to-purple-600',
      items: services.website.items.slice(0, 2),
      bgPattern: 'bg-pattern-grid'
    },
    {
      title: services.crypto.title,
      subtitle: services.crypto.subtitle,
      icon: Coins,
      path: '/kripto-hizmetleri',
      gradient: 'from-purple-500 via-pink-500 to-pink-600',
      items: services.crypto.items.slice(0, 2),
      bgPattern: 'bg-pattern-dots'
    },
    {
      title: services.design.title,
      subtitle: services.design.subtitle,
      icon: Palette,
      path: '/tasarim-hizmetleri',
      gradient: 'from-pink-500 via-red-500 to-orange-500',
      items: services.design.items.slice(0, 2),
      bgPattern: 'bg-pattern-grid'
    }
  ];

  const processIcons = {
    FileText,
    CheckCircle,
    CreditCard,
    Settings,
    Headphones
  };

  const stats = [
    { icon: Users, value: '500+', label: 'Mutlu Müşteri' },
    { icon: Award, value: '1000+', label: 'Tamamlanan Proje' },
    { icon: TrendingUp, value: '%99', label: 'Müşteri Memnuniyeti' },
    { icon: Shield, value: '7/24', label: 'Destek Hizmeti' }
  ];

  return (
    <div className="min-h-screen overflow-hidden bg-white dark:bg-slate-900 transition-colors duration-300">
      <SEO 
        title="Anasayfa | Dijital Strateji & Blockchain Çözümleri"
        description="Viral Strateji: Modern web tasarımı, kripto token geliştirme ve kurumsal kimlik hizmetleri. Dijital dünyada markanızı öne çıkarın."
        keywords="dijital ajans, blockchain ajansı, web tasarım istanbul, kripto token uzmanı, kurumsal kimlik tasarımı"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <AnimatedBackground variant="particles" />
        <AnimatedBackground variant="gradient" />

        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center">
            {/* Floating badge */}
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-full shadow-lg mb-8 animate-fade-in-down border dark:border-slate-700">
              <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-semibold gradient-text dark:text-purple-300">Dijital Dünyada Fark Yaratın</span>
            </div>

            {/* Main Heading with typing animation */}
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              <span className="gradient-text-neon">
                {typedText}
              </span>
              <span className="animate-pulse">|</span>
            </h1>

            <p className="text-xl sm:text-2xl text-gray-600 dark:text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto animate-fade-in-up">
              {company.description}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <Link
                to="/portfolyo"
                className="group relative overflow-hidden bg-white dark:bg-slate-800 text-gray-900 dark:text-white border-2 border-transparent px-8 py-3 rounded-full font-semibold text-base shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center space-x-2"
              >
                <span className="relative z-10 bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-pink-600 group-hover:text-white transition-colors duration-300">
                  İşlerimizi İnceleyin
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-0" />
              </Link>

              <Link
                to="/iletisim"
                className="group relative overflow-hidden bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-size-200 animate-gradient text-white px-8 py-3 rounded-full font-semibold text-base shadow-lg hover:shadow-neon transition-all duration-300 transform hover:scale-105 flex items-center space-x-2"
              >
                <span className="relative z-10">Hemen Başlayın</span>
                <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
            </div>

            {/* Quota / Urgency Badge */}
            <div className="inline-flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-full mb-12 animate-pulse shadow-lg">
              <Calendar className="w-5 h-5 text-red-500" />
              <span className="text-sm font-bold text-red-600 dark:text-red-400">
                Bu Hafta İçin Kalan Kontenjan: <span className="text-lg">2</span>
              </span>
            </div>

            {/* Floating Elements */}
            <div className="absolute top-20 left-10 w-20 h-20 bg-purple-400 rounded-full filter blur-2xl opacity-30 animate-float" />
            <div className="absolute bottom-40 right-10 w-32 h-32 bg-pink-400 rounded-full filter blur-3xl opacity-30 animate-float-slow" />
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-blue-400 rounded-full filter blur-2xl opacity-30 animate-float" />
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
            <div className="flex flex-col items-center space-y-2 text-purple-600">
              <span className="text-sm font-medium">Keşfet</span>
              <div className="w-6 h-10 rounded-full border-2 border-purple-600 flex items-start justify-center p-2">
                <div className="w-1.5 h-3 bg-purple-600 rounded-full animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Marquee */}
      <TechnologyMarquee />

      {/* Stats Section */}
      <section className="relative py-20 bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-pattern-dots opacity-20" />
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="text-center group animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl mb-4 group-hover:bg-white/30 transition-all duration-300 group-hover:scale-110 transform">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-4xl font-bold mb-2 group-hover:scale-110 transition-transform duration-300" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>{stat.value}</div>
                  <div className="text-purple-100 font-medium" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.2)' }}>{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-purple-50 dark:from-slate-800 dark:to-slate-900 relative overflow-hidden transition-colors duration-300">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-purple-100/50 dark:from-purple-900/30 to-transparent" />

        <div className="container mx-auto relative">
          <div className="text-center mb-20 animate-fade-in-up">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/50 rounded-full mb-6 border dark:border-purple-700">
              <Zap className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">Hizmetlerimiz</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              <span className="gradient-text-neon">Dijital Çözümlerimiz</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Website geliştirmeden blockchain çözümlerine, tasarımdan pazarlama stratejilerine kadar
              dijital dünyada ihtiyacınız olan her şey.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {serviceCards.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="group relative bg-white dark:bg-slate-800 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl dark:shadow-2xl transition-all duration-500 transform hover:-translate-y-4 animate-fade-in-up border dark:border-slate-700"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  {/* Gradient top border with animation */}
                  <div className={`h-2 bg-gradient-to-r ${service.gradient}`} />

                  {/* Background pattern */}
                  <div className={`absolute top-0 right-0 w-32 h-32 ${service.bgPattern} opacity-5`} />

                  <div className="p-6 sm:p-8 relative">
                    {/* Icon with gradient background */}
                    <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br ${service.gradient} rounded-2xl mb-6 shadow-colored group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                      <Icon className="w-10 h-10 text-white" />
                    </div>

                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:gradient-text transition-all duration-300">
                      {service.title}
                    </h3>

                    <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                      {service.subtitle}
                    </p>

                    {/* Service items */}
                    <div className="space-y-4 mb-8">
                      {service.items.map((item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 gap-2 sm:gap-0 bg-gray-50 dark:bg-slate-700/50 rounded-xl group-hover:bg-purple-50 dark:group-hover:bg-purple-900/30 transition-colors duration-300"
                        >
                          <span className="text-gray-700 dark:text-gray-200 font-medium flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span>{item.title}</span>
                          </span>
                          <span className="text-purple-600 font-bold">{item.price}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <Link
                      to={service.path}
                      className={`block w-full bg-gradient-to-r ${service.gradient} text-white py-4 px-6 rounded-xl font-bold text-center hover:shadow-2xl transition-all duration-300 transform group-hover:scale-105 relative overflow-hidden`}
                    >
                      <span className="relative z-10 flex items-center justify-center space-x-2">
                        <span>Detayları Görüntüle</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                      </span>
                      <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                    </Link>
                  </div>

                  {/* Hover glow effect */}
                  <div className={`absolute -inset-0.5 bg-gradient-to-r ${service.gradient} rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500 -z-10`} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lead Magnet Section */}
      <section className="py-16 bg-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern-grid opacity-10" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-white max-w-2xl text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Web Siteniz Satış Yapıyor mu?</h2>
              <p className="text-lg text-purple-100 opacity-90">
                Mevcut web sitenizi ücretsiz analiz edelim. Hız, SEO ve kullanıcı deneyimi raporunuzu 24 saat içinde gönderelim.
              </p>
            </div>
            <Link
              to="/iletisim"
              className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl whitespace-nowrap"
            >
              Ücretsiz Analiz İsteyin
            </Link>
          </div>
        </div>
      </section>

      {/* Order Process */}
      <section id="process" className="py-24 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800 relative overflow-hidden transition-colors duration-300">
        <div className="absolute inset-0 bg-pattern-dots opacity-30 dark:opacity-10" />

        <div className="container mx-auto relative">
          <div className="text-center mb-20 animate-fade-in-up">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/50 rounded-full mb-6 border dark:border-purple-700">
              <Settings className="w-5 h-5 text-purple-600 dark:text-purple-400 animate-spin" style={{ animationDuration: '3s' }} />
              <span className="text-sm font-semibold text-purple-700 dark:text-purple-300">Süreç</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              <span className="gradient-text-neon">Sipariş Süreci Nasıl İşler?</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Basit 5 adımda projenizi hayata geçirin. Her aşamada sizinle iletişim halinde oluyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {orderProcess.map((step, index) => {
              const Icon = processIcons[step.icon];
              return (
                <div
                  key={index}
                  className="text-center group animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="relative mb-6">
                    {/* Connecting line */}
                    {index < orderProcess.length - 1 && (
                      <div className="hidden lg:block absolute top-10 left-1/2 w-full h-1 bg-gradient-to-r from-purple-500 to-pink-500 opacity-30" />
                    )}

                    {/* Step circle */}
                    <div className="relative inline-block">
                      <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-purple-600 w-24 h-24 rounded-2xl flex items-center justify-center shadow-xl group-hover:shadow-2xl transition-all duration-300 transform group-hover:scale-110 group-hover:rotate-6 relative z-10">
                        <Icon className="w-12 h-12 text-white" />
                      </div>

                      {/* Step number badge */}
                      <div className="absolute -top-3 -right-3 bg-white border-4 border-purple-600 text-purple-600 w-10 h-10 rounded-full flex items-center justify-center text-lg font-black shadow-lg z-20">
                        {step.step}
                      </div>

                      {/* Glow effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl opacity-0 group-hover:opacity-50 blur-xl transition-opacity duration-300" />
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 group-hover:gradient-text transition-all duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-600 via-pink-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl opacity-20 animate-float" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-pink-400 rounded-full filter blur-3xl opacity-20 animate-float-slow" />
        </div>

        <div className="container mx-auto text-center relative">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 animate-fade-in-up">
            Projenizi Hayata Geçirmeye Hazır mısınız?
          </h2>
          <p className="text-xl mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.1s', color: '#ffffff', opacity: 1 }}>
            Uzman ekibimizle görüşerek size özel çözümler geliştirin. Ücretsiz danışmanlık için hemen iletişime geçin.
          </p>

          <Link
            to="/iletisim"
            className="inline-flex items-center space-x-2 bg-white text-purple-600 px-8 py-3.5 rounded-full font-bold text-base hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 shadow-xl group animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            <span>Ücretsiz Danışmanlık</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;